<template>
  <div>
    <div class="coupons-surface">
      <div v-if="loading" class="px-3 py-4 text-xs-center">Loading...</div>

      <template v-else>
        <div v-if="!cards.length" class="px-3 py-3">
          <div class="mb-2">No DiscCard benefit found for this account.</div>
          <v-btn
            small
            color="success"
            class="my-2"
            :disabled="!isCreateEnabled"
            @click="openCreateDialog"
          >
            Create
          </v-btn>
          <div v-if="isCreateAllowed && !canCreate" class="caption">
            A DiscCard cannot be added for this term.
          </div>
        </div>

        <div v-else class="px-3 py-3">
          <div
            v-for="(card, index) in cards"
            :key="card.id || `disccard-${index}`"
            class="coupon-row"
          >
            <v-layout row wrap>
              <v-flex xs12 sm8>
                <div class="coupon-title mb-2">
                  DiscCard Benefit
                </div>
                <div class="field-row">
                  <span class="field-label">Date</span>
                  <span class="field-value">{{ formatDate(card.date) }}</span>
                </div>
                <div class="field-row">
                  <span class="field-label">Created by</span>
                  <span class="field-value">{{ card.enteredBy || "-" }}</span>
                </div>
                <div class="field-row">
                  <span class="field-label">Term</span>
                  <span class="field-value">{{ card.regID || "-" }}</span>
                </div>
              </v-flex>
              <v-flex xs12 sm4 class="mt-2 mt-sm-0">
                <div class="amount-label">Activation code</div>
                <div class="amount-value">
                  {{ card.activationCode || "-" }}
                </div>
                <div class="disc-card-status mt-2">
                  {{ statusLabel(card) }}
                </div>
              </v-flex>
              <v-flex xs12>
                <div class="coupon-note mt-2">
                  <div class="note-label">Note</div>
                  <div class="note-value">{{ card.note || "-" }}</div>
                </div>
              </v-flex>
              <v-flex xs12 class="mt-2">
                <div class="meta-row">
                  <span class="meta-chip">
                    Emailed: {{ formatDate(card.emailedAt) }}
                  </span>
                  <span class="meta-chip">
                    Synced: {{ formatDate(card.syncedAt) }}
                  </span>
                  <span class="meta-chip">
                    Activated in Tuition Portal:
                    {{ formatDate(card.activatedAt) }}
                  </span>
                </div>
              </v-flex>
            </v-layout>

            <v-layout row wrap class="mt-3">
              <v-btn
                v-if="!card.syncedAt"
                small
                color="success"
                class="mr-2 mb-2"
                :loading="processing"
                @click="syncCard(card)"
              >
                Sync
              </v-btn>
              <v-btn
                v-if="!card.isActivated"
                small
                outline
                color="success"
                class="mr-2 mb-2"
                @click="openEmailDialog(card)"
              >
                Email Customer
              </v-btn>
            </v-layout>

            <v-divider
              v-if="index + 1 < cards.length"
              class="mt-4"
            ></v-divider>
          </div>

          <v-btn
            v-if="isCreateEnabled"
            small
            color="success"
            class="mt-3"
            @click="openCreateDialog"
          >
            Create
          </v-btn>
        </div>
      </template>
    </div>

    <v-dialog v-model="createDialog" max-width="400">
      <v-card class="pa-2">
        <v-card-title class="headline pb-1">Create DiscCard Benefit</v-card-title>
        <v-card-text class="pt-3 pb-0">
          <div class="caption mb-3">
            Adds a DiscCard for this account
            <span v-if="currentRegID"> ({{ currentRegID }})</span>.
            The activation code is generated automatically and synced to
            DiscCard.
          </div>
          <v-text-field
            ref="cardNote"
            v-model="createForm.note"
            label="Note"
            :rules="requiredRules"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="createDialog = false">
            Cancel
          </v-btn>
          <v-btn color="success" :loading="processing" @click="createCard">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="emailDialog" max-width="460">
      <v-card class="pa-2">
        <v-card-title class="headline pb-1">
          Send DiscCard To Customer
        </v-card-title>
        <v-card-text class="pt-2 pb-2">
          <div class="caption mb-2">
            Send the same activation-code email as the mass DiscCard email.
          </div>
          <div class="dialog-summary mb-3">
            <div class="download-summary-label">Activation code</div>
            <div class="download-summary-value">
              {{ selectedCard.activationCode || "-" }}
            </div>
          </div>
          <v-text-field
            ref="cardEmail"
            v-model="emailForm.email"
            label="Email"
            :rules="emailRules"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pt-1">
          <v-spacer></v-spacer>
          <v-btn color="default" dark class="mr-2" @click="emailDialog = false">
            Cancel
          </v-btn>
          <v-btn color="success" :loading="processing" @click="sendCardEmail">
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";

export default {
  props: {
    accountID: String,
    accountEmail: String,
  },
  data() {
    return {
      loading: false,
      processing: false,
      cards: [],
      currentRegID: "",
      canCreate: false,
      createDialog: false,
      emailDialog: false,
      selectedCard: {},
      createForm: {
        note: "",
      },
      emailForm: {
        email: "",
      },
      requiredRules: [(v) => !!v || "Required"],
      emailRules: [
        (v) => !!v || "Required",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ],
    };
  },
  computed: {
    isCreateAllowed() {
      return this.userHasRole("DiscCardCreate") || this.isAdminUser();
    },
    isCreateEnabled() {
      return this.isCreateAllowed && this.canCreate;
    },
  },
  created() {
    this.loadData();
    if (this.accountEmail) {
      this.emailForm.email = this.accountEmail;
    }
  },
  watch: {
    accountEmail(newValue) {
      if (!this.emailForm.email) {
        this.emailForm.email = newValue || "";
      }
    },
  },
  methods: {
    async loadData() {
      if (!this.accountID) return;

      try {
        this.loading = true;
        const response = await this.$http.get(
          "api/discCards/GetForAccount?accountID=" + this.accountID.trim()
        );
        this.cards =
          response && Array.isArray(response.cards) ? response.cards : [];
        this.currentRegID =
          response && response.currentRegID ? response.currentRegID : "";
        this.canCreate = !!(response && response.canCreate);
      } catch (e) {
        this.cards = [];
        this.canCreate = false;
        this.$notify({
          group: "notification",
          text: "Failed to load DiscCard benefits",
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return "-";
      return moment(value).format("LLL");
    },
    statusLabel(card) {
      if (!card) return "";
      if (card.isActivated || card.activatedAt) return "Activated";
      if (card.syncedAt) return "Ready to activate";
      return "Pending sync";
    },
    userHasRole(roleName) {
      const roles = Array.isArray(this.$store.state.authentication.roles)
        ? this.$store.state.authentication.roles
        : this.$store.state.authentication.roles
        ? this.$store.state.authentication.roles.split(",")
        : [];

      return roles.includes(roleName);
    },
    isAdminUser() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];
      return !!isAdmin && isAdmin != "false";
    },
    openCreateDialog() {
      if (!this.isCreateEnabled) return;

      this.createForm.note = "";
      this.createDialog = true;

      this.$nextTick(() => {
        if (this.$refs.cardNote) this.$refs.cardNote.resetValidation();
      });
    },
    async createCard() {
      if (!this.isCreateEnabled) return;
      if (!this.$refs.cardNote.validate(true)) return;

      const note = (this.createForm.note || "").trim();
      if (!note) return;

      try {
        this.processing = true;
        await this.$http.post("api/discCards/Create", {
          accountID: this.accountID.trim(),
          note: note,
          enteredBy: this.$store.state.authentication.user,
        });

        this.createDialog = false;
        this.$notify({
          group: "notification",
          text: "DiscCard benefit created",
          type: "success",
        });
        await this.loadData();
      } catch (e) {
        // Global axios handler already shows the error
      } finally {
        this.processing = false;
      }
    },
    async syncCard(card) {
      if (!card || card.syncedAt) return;

      try {
        this.processing = true;
        await this.$http.post("api/discCards/SyncForAccount", {
          accountID: this.accountID.trim(),
        });
        this.$notify({
          group: "notification",
          text: "DiscCard synced",
          type: "success",
        });
        await this.loadData();
      } catch (e) {
        // Global axios handler already shows the error
      } finally {
        this.processing = false;
      }
    },
    notifyEmailBlocked(card) {
      if (!card) {
        this.$notify({
          group: "notification",
          text: "No DiscCard was found for this account.",
          type: "error",
        });
        return true;
      }

      if (card.isActivated || card.activatedAt) {
        this.$notify({
          group: "notification",
          text:
            "This DiscCard is already activated. The activation email is not sent after activation.",
          type: "error",
        });
        return true;
      }

      if (!card.syncedAt) {
        this.$notify({
          group: "notification",
          text:
            "This DiscCard has not been synced yet. Email cannot be sent until it is synced.",
          type: "error",
        });
        return true;
      }

      return false;
    },
    openEmailDialog(card) {
      if (this.notifyEmailBlocked(card)) return;

      this.selectedCard = card || {};
      this.emailForm.email = this.accountEmail || "";
      this.emailDialog = true;

      this.$nextTick(() => {
        if (this.$refs.cardEmail) this.$refs.cardEmail.resetValidation();
      });
    },
    async sendCardEmail() {
      if (!this.$refs.cardEmail.validate(true)) return;
      if (this.notifyEmailBlocked(this.selectedCard)) return;

      try {
        this.processing = true;
        await this.$http.post("api/discCards/Email", {
          accountID: this.accountID.trim(),
          to: (this.emailForm.email || "").trim(),
        });

        this.emailDialog = false;
        this.$notify({
          group: "notification",
          text: "DiscCard email sent",
          type: "success",
        });
        await this.loadData();
      } catch (e) {
        // Global axios handler already shows the error
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<style>
.disc-card-status {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
</style>
